
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface TeamMember {
  id: number;
  name: string;
  role: string;
  category: 'sales' | 'drivers' | 'crew';
  experience: number;
  bio: string;
  image: string;
  email: string;
  phone: string;
}

const STORAGE_KEY = 'lonetrs_team_members';

const defaultEmpty: TeamMember[] = [];

function loadTeam(): TeamMember[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    // Storage unavailable or data corrupted, return empty array
  }
  return defaultEmpty;
}

function saveTeam(members: TeamMember[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(members));
}

interface EditableFieldProps {
  value: string | number;
  onSave: (val: string) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  type?: string;
}

function EditableField({ value, onSave, placeholder, className, multiline, type }: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && ref.current) ref.current.focus();
  }, [editing]);

  const commit = () => {
    onSave(draft);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(String(value));
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
        {multiline ? (
          <textarea
            ref={ref as any}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') cancel(); }}
            rows={3}
            className={`bg-white/10 border border-electric-blue/60 rounded-lg px-2 py-1 text-white text-sm resize-none outline-none focus:border-electric-blue w-full ${className ?? ''}`}
          />
        ) : (
          <input
            ref={ref as any}
            type={type ?? 'text'}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
            className={`bg-white/10 border border-electric-blue/60 rounded-lg px-2 py-1 text-white text-sm outline-none focus:border-electric-blue w-full ${className ?? ''}`}
          />
        )}
        <div className="flex gap-2">
          <button onClick={commit} className="text-xs bg-electric-blue text-dark-900 px-3 py-1 rounded-full font-semibold whitespace-nowrap cursor-pointer hover:opacity-90">✓ Save</button>
          <button onClick={cancel} className="text-xs bg-white/10 text-white px-3 py-1 rounded-full whitespace-nowrap cursor-pointer hover:bg-white/20">✗ Cancel</button>
        </div>
      </div>
    );
  }

  const display = String(value).trim();
  return (
    <span
      onClick={() => { setDraft(String(value)); setEditing(true); }}
      title="Click to edit"
      className={`cursor-pointer group/edit relative inline-block hover:opacity-80 transition-opacity ${className ?? ''}`}
    >
      {display || <span className="italic opacity-40">{placeholder ?? 'Click to edit'}</span>}
      <i className="ri-pencil-line ml-1 text-electric-blue/60 text-xs opacity-0 group-hover/edit:opacity-100 transition-opacity" />
    </span>
  );
}

const CATEGORIES = [
  { id: 'all', label: 'All Team' },
  { id: 'sales', label: 'Sales Team' },
  { id: 'drivers', label: 'Drivers' },
  { id: 'crew', label: 'Crew Members' },
];

export default function TeamMembers() {
  const [members, setMembers] = useState<TeamMember[]>(loadTeam);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'sales' | 'drivers' | 'crew'>('all');
  const [editingImage, setEditingImage] = useState<number | null>(null);
  const [imageUrlDraft, setImageUrlDraft] = useState('');

  const persist = (updated: TeamMember[]) => {
    setMembers(updated);
    saveTeam(updated);
  };

  const updateField = (id: number, field: keyof TeamMember, val: string) => {
    persist(members.map(m => m.id === id ? { ...m, [field]: field === 'experience' ? Number(val) || 0 : val } : m));
  };

  const addMember = () => {
    const newMember: TeamMember = {
      id: Date.now(),
      name: '',
      role: '',
      category: 'crew',
      experience: 0,
      bio: '',
      image: '',
      email: '',
      phone: '',
    };
    persist([...members, newMember]);
  };

  const deleteMember = (id: number) => {
    persist(members.filter(m => m.id !== id));
  };

  const updateCategory = (id: number, cat: TeamMember['category']) => {
    persist(members.map(m => m.id === id ? { ...m, category: cat } : m));
  };

  const openImageEdit = (id: number, current: string) => {
    setEditingImage(id);
    setImageUrlDraft(current);
  };

  const saveImage = (id: number) => {
    updateField(id, 'image', imageUrlDraft);
    setEditingImage(null);
  };

  const filtered = selectedCategory === 'all'
    ? members
    : members.filter(m => m.category === selectedCategory);

  return (
    <section className="py-24 bg-dark-900 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            MEET OUR <span className="text-electric-blue">TEAM</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-2">
            Click any field on a card to edit it. Add your real team members below.
          </p>
          <p className="font-inter text-electric-blue/70 text-sm mb-8">
            <i className="ri-save-line mr-1" /> All changes save automatically to your browser.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id as any)}
                className={`px-6 py-2.5 rounded-full font-orbitron text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 shadow-lg shadow-electric-blue/50'
                    : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </motion.div>

        {members.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-white/20 rounded-2xl mb-8">
            <i className="ri-team-line text-white/30 text-6xl mb-4 block" />
            <p className="font-inter text-white/40 text-lg mb-2">No team members yet.</p>
            <p className="font-inter text-white/30 text-sm">Click "Add Team Member" below to get started.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <AnimatePresence>
            {filtered.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group"
              >
                <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:bg-white/15 hover:border-electric-blue/50 transition-all duration-300 hover:shadow-xl hover:shadow-electric-blue/20 relative">
                  
                  {/* Delete Button */}
                  <button
                    onClick={() => deleteMember(member.id)}
                    title="Remove member"
                    className="absolute top-3 left-3 z-20 w-7 h-7 flex items-center justify-center bg-red-500/80 hover:bg-red-500 text-white rounded-full text-xs cursor-pointer transition-all opacity-0 group-hover:opacity-100 whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>

                  {/* Photo Area */}
                  <div className="relative h-56 w-full overflow-hidden bg-white/5">
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name || 'Team member'}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-white/20">
                        <i className="ri-user-3-line text-5xl mb-2" />
                        <span className="text-xs font-inter">No photo</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent opacity-60" />

                    {/* Edit Photo Button */}
                    <button
                      onClick={() => openImageEdit(member.id, member.image)}
                      className="absolute bottom-3 right-3 z-10 flex items-center gap-1 bg-electric-blue/90 hover:bg-electric-blue text-dark-900 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-all whitespace-nowrap"
                    >
                      <i className="ri-image-edit-line text-sm" /> Photo
                    </button>

                    {/* Experience Badge */}
                    <div className="absolute top-3 right-3 px-3 py-1.5 bg-electric-blue/90 backdrop-blur-md rounded-full">
                      <span className="font-orbitron text-dark-900 text-xs font-bold flex items-center gap-1">
                        <EditableField
                          value={member.experience}
                          onSave={v => updateField(member.id, 'experience', v)}
                          placeholder="0"
                          className="font-orbitron text-dark-900 text-xs font-bold w-8"
                          type="number"
                        />
                        + YRS
                      </span>
                    </div>
                  </div>

                  {/* Image URL Modal */}
                  {editingImage === member.id && (
                    <div className="absolute inset-0 z-30 bg-dark-900/95 flex flex-col items-center justify-center p-6 rounded-2xl">
                      <p className="font-inter text-white text-sm mb-3 text-center">Paste a photo URL:</p>
                      <input
                        type="url"
                        value={imageUrlDraft}
                        onChange={e => setImageUrlDraft(e.target.value)}
                        placeholder="https://..."
                        className="w-full bg-white/10 border border-electric-blue/60 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-electric-blue mb-4"
                        autoFocus
                      />
                      <div className="flex gap-3">
                        <button onClick={() => saveImage(member.id)} className="bg-electric-blue text-dark-900 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap hover:opacity-90">Save</button>
                        <button onClick={() => setEditingImage(null)} className="bg-white/10 text-white px-4 py-2 rounded-full text-sm cursor-pointer whitespace-nowrap hover:bg-white/20">Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="p-5">
                    {/* Name */}
                    <h3 className="font-orbitron text-lg font-bold text-white mb-1">
                      <EditableField
                        value={member.name}
                        onSave={v => updateField(member.id, 'name', v)}
                        placeholder="Full Name"
                        className="font-orbitron text-lg font-bold text-white"
                      />
                    </h3>

                    {/* Role */}
                    <div className="font-inter text-electric-blue text-sm font-semibold mb-2">
                      <EditableField
                        value={member.role}
                        onSave={v => updateField(member.id, 'role', v)}
                        placeholder="Job Title / Role"
                        className="text-electric-blue text-sm font-semibold"
                      />
                    </div>

                    {/* Category Selector */}
                    <div className="mb-3">
                      <select
                        value={member.category}
                        onChange={e => updateCategory(member.id, e.target.value as TeamMember['category'])}
                        className="bg-white/10 border border-white/20 text-white/70 text-xs rounded-full px-3 py-1 outline-none cursor-pointer font-inter"
                      >
                        <option value="sales" className="bg-dark-900">Sales Team</option>
                        <option value="drivers" className="bg-dark-900">Drivers</option>
                        <option value="crew" className="bg-dark-900">Crew Members</option>
                      </select>
                    </div>

                    {/* Bio */}
                    <div className="font-inter text-white/70 text-sm leading-relaxed mb-4">
                      <EditableField
                        value={member.bio}
                        onSave={v => updateField(member.id, 'bio', v)}
                        placeholder="Short bio about this team member..."
                        className="text-white/70 text-sm"
                        multiline
                      />
                    </div>

                    {/* Contact */}
                    <div className="pt-4 border-t border-white/10 space-y-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-mail-line text-electric-blue text-base w-5 h-5 flex items-center justify-center" />
                        <span className="text-white/60 text-xs font-inter flex-1">
                          <EditableField
                            value={member.email}
                            onSave={v => updateField(member.id, 'email', v)}
                            placeholder="Email address"
                            className="text-white/60 text-xs"
                            type="email"
                          />
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-phone-line text-electric-blue text-base w-5 h-5 flex items-center justify-center" />
                        <span className="text-white/60 text-xs font-inter flex-1">
                          <EditableField
                            value={member.phone}
                            onSave={v => updateField(member.id, 'phone', v)}
                            placeholder="Phone number"
                            className="text-white/60 text-xs"
                            type="tel"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Add Member Button */}
        <div className="flex justify-center mt-10">
          <button
            onClick={addMember}
            className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 font-orbitron font-bold text-sm rounded-full shadow-lg shadow-electric-blue/40 hover:shadow-electric-blue/60 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line text-lg" />
            Add Team Member
          </button>
        </div>
      </div>
    </section>
  );
}
